import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import LifeCardV2 from '../../../components/common/LifeCard/LifeCardV2';
import LifeCardSkeletonV2 from '../../../components/common/LifeCard/LifeCardSkeletonV2';
import { getLifeCampaigns } from '../../../api/services/campaignService';
import { canViewPrivateCampaign } from '../../../utils/authUtils';

const EMERGENCY_SUPPORT_CMS_SECTION_ID = 95;

const EmergencySupport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery('(min-width: 1390px)'); // Grid at 1390px+ (match Home Urgent campaigns)

  const home = useSelector((state) => state.cms.home);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [lifeCampaigns, setLifeCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === EMERGENCY_SUPPORT_CMS_SECTION_ID);
    if (!section || section.status === 0) return null;
    return {
      title: section.title ?? '',
      sub_title: section.sub_title ?? '',
    };
  }, [home]);

  useEffect(() => {
    let isMounted = true;
    const fetchLife = async () => {
      try {
        setLoading(true);
        const response = await getLifeCampaigns();
        if (isMounted) {
          setLifeCampaigns(response?.data || []);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching life campaigns for Medical Emergency:', err);
          setLifeCampaigns([]);
          setError('Unable to load campaigns.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchLife();
    return () => {
      isMounted = false;
    };
  }, []);

  const MAX_LIFE_CARDS = 6;
  const latestLifeCampaigns = useMemo(() => {
    const list = lifeCampaigns || [];
    const privacyFiltered = list.filter((c) => {
      if (c.IsPrivate === 0 || !c.IsPrivate) return true;
      if (c.IsPrivate === 1) return canViewPrivateCampaign(user, isAuthenticated);
      return true;
    });
    const medicalEmergencyOnly = privacyFiltered.filter(
      (c) => Number(c.latestLifeCampaign) === 1
    );
    const byUpdatedNewest = [...medicalEmergencyOnly].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.updated_at || a.createdAt || a.created_at || 0).getTime();
      const dateB = new Date(b.updatedAt || b.updated_at || b.createdAt || b.created_at || 0).getTime();
      return dateB - dateA;
    });
    return byUpdatedNewest.slice(0, MAX_LIFE_CARDS);
  }, [lifeCampaigns, user, isAuthenticated]);

  // Responsive styles (match Home Urgent campaigns: mobile full-width + 16px padding, desktop 94.44%)
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          width: '100%',
          paddingTop: '26px',
          paddingBottom: '20px',
          paddingLeft: '16px',
          paddingRight: '16px',
          borderRadius: '16px'
        },
        title: {
          fontSize: '18px',
          lineHeight: '16.18px',
          letterSpacing: '-2%',
          marginBottom: '10px',
          paddingLeft: '0px'
        },
        description: {
          fontSize: '10px',
          lineHeight: '19.42px',
          letterSpacing: '-2%',
          marginBottom: '20px',
          paddingLeft: '0px'
        },
        sectionHeader: {
          fontSize: '10px',
          lineHeight: '100%',
          letterSpacing: '2px',
          marginLeft: '0px',
          marginBottom: '10px'
        }
      };
    } else if (!isDesktop) {
      return {
        container: {
          width: '100%',
          borderRadius: '16px',
          paddingTop: '26px',
          paddingBottom: '20px',
          paddingLeft: '16px',
          paddingRight: '16px'
        },
        title: {
          fontSize: '22px',
          marginBottom: '8px',
          paddingLeft: '0px'
        },
        description: {
          fontSize: '12px',
          lineHeight: '20px',
          marginBottom: '20px',
          paddingLeft: '0px'
        },
        sectionHeader: {
          fontSize: '12px',
          marginLeft: '0px',
          marginBottom: '12px'
        }
      };
    } else {
      return {
        container: {
          width: '94.44%',
          borderRadius: '36px',
          paddingTop: '35px',
          paddingBottom: '50px',
        },
        title: {
          fontSize: '36px',
          marginBottom: '10px',
          paddingLeft: '40px'
        },
        description: {
          fontSize: '16px',
          lineHeight: '24px',
          marginBottom: '40px',
          paddingLeft: '40px'
        },
        sectionHeader: {
          fontSize: '14px',
          marginLeft: '40px',
          marginBottom: '16px'
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <Box
      sx={{
        ...styles.container,
        maxWidth: '1440px',
        background: '#F7F7F7',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: isMobile ? '20px' : '40px',
        marginBottom: isMobile ? '30px' : '60px',
      }}
    >
      {/* EMERGENCY SUPPORT */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? '8px' : '10px', 
        alignSelf: 'flex-start', 
        marginLeft: styles.sectionHeader.marginLeft,
        marginBottom: styles.sectionHeader.marginBottom
      }}>
        <Box
          sx={{
            width: isMobile ? '6px' : '8px',
            height: isMobile ? '6px' : '8px',
            borderRadius: '50%',
            backgroundColor: '#A9C74E',
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Arial',
            fontWeight: 400,
            fontSize: styles.sectionHeader.fontSize,
            lineHeight: styles.sectionHeader.lineHeight,
            letterSpacing: styles.sectionHeader.letterSpacing,
            verticalAlign: 'middle',
            textTransform: 'uppercase',
            color: '#131313',
          }}
        >
          EMERGENCY SUPPORT
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: styles.title.fontSize,
          lineHeight: styles.title.lineHeight,
          letterSpacing: styles.title.letterSpacing,
          verticalAlign: 'middle',
          textTransform: 'capitalize',
          color: '#1D1D1B',
          paddingLeft: styles.title.paddingLeft,
          alignSelf: 'flex-start',
          marginBottom: styles.title.marginBottom,
          textAlign: 'center'
        }}
      >
        {cms?.title ?? ""}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: styles.description.fontSize,
          lineHeight: styles.description.lineHeight,
          letterSpacing: styles.description.letterSpacing,
          verticalAlign: 'middle',
          color: '#7D7D7D',
          maxWidth: isMobile ? '100%' : '1161px',
          textAlign: 'left',
          alignSelf: 'flex-start',
          paddingLeft: styles.description.paddingLeft,
          marginBottom: styles.description.marginBottom
        }}
      >
        {cms?.sub_title ?? ""}
      </Typography>

      {/* Life campaign cards – API-backed life campaigns; click goes to life detail */}
      <Box
        className="hide-scrollbar"
        sx={{
          display: isDesktop ? 'grid' : 'flex',
          gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'none',
          gridTemplateRows: isDesktop ? 'repeat(2, auto)' : 'none',
          width: '100%',
          justifyContent: isDesktop ? 'center' : 'flex-start',
          alignItems: 'center',
          gap: isMobile ? '16px' : isTablet ? '20px' : '24px',
          marginBottom: isMobile ? '20px' : '48px',
          paddingLeft: isMobile ? '0' : '34px',
          paddingRight: isMobile ? '0' : '34px',
          overflowX: isDesktop ? 'hidden' : 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {loading ? (
          Array.from({ length: MAX_LIFE_CARDS }).map((_, index) => (
            <LifeCardSkeletonV2 key={`life-skeleton-${index}`} />
          ))
        ) : error ? (
          <Typography
            color="text.secondary"
            sx={{
              px: 2,
              gridColumn: isDesktop ? '1 / -1' : 'none',
              width: '100%',
              textAlign: 'center',
              py: 3
            }}
          >
            {error}
          </Typography>
        ) : (
          latestLifeCampaigns.map((item) => (
            <LifeCardV2 key={item.id} lifeData={item} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default EmergencySupport;


